/**
 * CircleEvents — Event list + detail for a Circle.
 * Route: /circles/:circleSlug/events
 *        /circles/:circleId/events/:eventId
 *
 * Members only. Admins can create / edit / cancel events.
 * Users can RSVP (going / maybe / notGoing) or withdraw.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowLeft, Calendar, Clock, MapPin, Repeat, Users,
  Check, HelpCircle, XCircle, Edit2, Slash, Plus, ChevronLeft,
} from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import { useCircleId } from '../hooks/useCircleId';
import GlassPanel from '../components/GlassPanel';
import MonoAvatar from '../components/MonoAvatar';
import {
  getCircleDetail,
  getCircleEvents,
  getCircleEventDetail,
  createCircleEvent,
  updateCircleEvent,
  cancelCircleEvent,
  rsvpToEvent,
  withdrawFromEvent,
} from '../services/circleService';
import type { Circle, CircleMember, CircleEvent, CircleEventRsvp } from '../types/circles';
import { formatTimestamp } from '../utils/dateUtils';
import '../styles/modern.css';

type RsvpStatus = 'going' | 'maybe' | 'notGoing';

// ──────────────────────────────────────────────────────────────────────────
// Events List
// ──────────────────────────────────────────────────────────────────────────

export default function CircleEvents() {
  const { circleSlug, eventId } = useParams<{ circleSlug: string; eventId?: string }>();
  const { circleId, resolving } = useCircleId(circleSlug);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();

  const [circle, setCircle] = useState<Circle | null>(null);
  const [membership, setMembership] = useState<CircleMember | null>(null);
  const [events, setEvents] = useState<CircleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Event detail sub-state
  const [eventDetail, setEventDetail] = useState<CircleEvent | null>(null);
  const [rsvps, setRsvps] = useState<CircleEventRsvp[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Create / edit sub-state
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CircleEvent | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dateTime: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    location: '',
    locationType: 'tbd' as 'physical' | 'virtual' | 'tbd',
    maxAttendees: '' as string,
    recurrence: 'none' as 'none' | 'daily' | 'weekly' | 'monthly',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const isAdmin = membership?.role === 'admin';
  const isMember = membership?.status === 'active';
  const uid = user?.uid || user?.userId;

  // ─── Auth gate ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load circle + events ────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!circleId || !isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, eventsRes] = await Promise.all([
        getCircleDetail(circleId),
        getCircleEvents(circleId),
      ]);
      setCircle(detail.circle);
      setMembership(detail.membership);
      if (detail.membership?.status !== 'active') {
        setError('You must be a member to view events.');
      }
      setEvents(eventsRes.events || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [circleId, isAuthenticated]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Load single event detail ─────────────────────────────────────────
  useEffect(() => {
    if (!circleId || !eventId || !isMember) return;
    setDetailLoading(true);
    (async () => {
      try {
        const res = await getCircleEventDetail(circleId, eventId);
        setEventDetail(res.event);
        setRsvps(res.rsvps || []);
      } catch (err: any) {
        setError(err?.message || 'Event not found');
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [circleId, eventId, isMember]);

  // ─── RSVP ─────────────────────────────────────────────────────────────
  const handleRsvp = async (status: RsvpStatus) => {
    if (!circleId || !eventId) return;
    setActionLoading(true);
    try {
      await rsvpToEvent(circleId, eventId, status);
      const res = await getCircleEventDetail(circleId, eventId);
      setEventDetail(res.event);
      setRsvps(res.rsvps || []);
    } catch (err: any) {
      alert(err?.message || 'Failed to RSVP');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!circleId || !eventId) return;
    setActionLoading(true);
    try {
      await withdrawFromEvent(circleId, eventId);
      const res = await getCircleEventDetail(circleId, eventId);
      setEventDetail(res.event);
      setRsvps(res.rsvps || []);
    } catch (err: any) {
      alert(err?.message || 'Failed to withdraw');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Cancel event (admin) ────────────────────────────────────────────
  const handleCancel = async (eid: string) => {
    if (!circleId || !confirm('Cancel this event?')) return;
    try {
      await cancelCircleEvent(circleId, eid);
      await loadData();
      if (eventId === eid) {
        navigate(`/circles/${circleSlug}/events`, { state: { circleId } });
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to cancel event');
    }
  };

  // ─── Create / edit event ──────────────────────────────────────────────
  const openCreateForm = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      dateTime: '',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      location: '',
      locationType: 'tbd',
      maxAttendees: '',
      recurrence: 'none',
    });
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (ev: CircleEvent) => {
    setEditingEvent(ev);
    setFormData({
      title: ev.title,
      description: ev.description || '',
      dateTime: ev.dateTime ? new Date(ev.dateTime as any).toISOString().slice(0, 16) : '',
      timezone: ev.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      location: ev.location || '',
      locationType: ev.locationType || 'tbd',
      maxAttendees: ev.maxAttendees ? String(ev.maxAttendees) : '',
      recurrence: ev.recurrence?.type ?? 'none',
    });
    setFormError(null);
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!circleId) return;
    setFormError(null);

    if (formData.title.length < 3 || formData.title.length > 100) {
      setFormError('Title must be 3-100 characters');
      return;
    }
    if (formData.description.length < 10 || formData.description.length > 1000) {
      setFormError('Description must be 10-1000 characters');
      return;
    }
    if (!formData.dateTime) {
      setFormError('Please select a date and time');
      return;
    }

    setActionLoading(true);
    try {
      const payload: any = {
        title: formData.title,
        description: formData.description,
        dateTime: new Date(formData.dateTime).toISOString(),
        timezone: formData.timezone,
        locationType: formData.locationType,
        // backend stores recurrence as {type, originalDateTime} and validates recurrence.type
        recurrence: formData.recurrence === 'none' ? null : { type: formData.recurrence },
      };
      if (formData.location) payload.location = formData.location;
      if (formData.maxAttendees) payload.maxAttendees = Number(formData.maxAttendees);

      if (editingEvent) {
        await updateCircleEvent(circleId, editingEvent.id, payload);
      } else {
        await createCircleEvent({ ...payload, circleId });
      }

      setShowForm(false);
      await loadData();
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save event');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────────
  const userRsvp = rsvps.find((r) => r.userId === uid);
  const goingCount = rsvps.filter((r) => r.status === 'going').length;
  const maybeCount = rsvps.filter((r) => r.status === 'maybe').length;

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  // ─── Single event detail view ─────────────────────────────────────────
  if (eventId && !showForm) {
    return (
      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 780 }}>
          {/* Back */}
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={() => navigate(`/circles/${circleSlug}/events`, { state: { circleId } })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 999, padding: '7px 14px', color: '#fff', fontSize: 13,
                fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <ChevronLeft size={14} /> events
            </button>
          </div>

          {detailLoading && (
            <GlassPanel hover={false}>
              <div className="skel-line wide pulse" style={{ height: 18, marginBottom: 10 }} />
              <div className="skel-line medium pulse" style={{ height: 12 }} />
            </GlassPanel>
          )}

          {error && !detailLoading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(220,200,240,0.7)' }}>
              <p>{error}</p>
            </div>
          )}

          {eventDetail && !detailLoading && (
            <GlassPanel hover={false} padding={0}>
              <div style={{
                height: 6,
                background: 'linear-gradient(90deg, #d946ef, #8b5cf6)',
                borderRadius: '16px 16px 0 0',
              }} />
              <div style={{ padding: 24 }}>
                <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: '0 0 16px', letterSpacing: '-0.025em' }}>
                  {eventDetail.title}
                </h1>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>
                    <Calendar size={14} />
                    <span>{eventDetail.dateTime ? formatTimestamp(eventDetail.dateTime) : '\u2014'}</span>
                  </div>
                  {eventDetail.timezone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>
                      <Clock size={14} />
                      <span>{eventDetail.timezone}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>
                    <MapPin size={14} />
                    <span>
                      {eventDetail.locationType === 'virtual' && 'Virtual'}
                      {eventDetail.locationType === 'physical' && (eventDetail.location || 'Physical')}
                      {eventDetail.locationType === 'tbd' && 'TBD'}
                    </span>
                  </div>
                  {eventDetail.recurrence && eventDetail.recurrence.type !== 'none' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>
                      <Repeat size={14} />
                      <span>{eventDetail.recurrence.type}</span>
                    </div>
                  )}
                  {eventDetail.maxAttendees && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(220,200,240,0.7)' }}>
                      <Users size={14} />
                      <span>{goingCount}/{eventDetail.maxAttendees} attending</span>
                    </div>
                  )}
                </div>

                <p style={{ fontSize: 14, lineHeight: 1.6, color: 'rgba(220,200,240,0.85)', margin: '0 0 20px', whiteSpace: 'pre-wrap' }}>
                  {eventDetail.description}
                </p>

                {/* RSVP Buttons */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                  padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginRight: 4 }}>RSVP:</span>
                  {(['going', 'maybe', 'notGoing'] as RsvpStatus[]).map((status) => (
                    <button
                      key={status}
                      className={userRsvp?.status === status ? 'primary-button' : undefined}
                      onClick={() => handleRsvp(status)}
                      disabled={actionLoading}
                      style={userRsvp?.status === status ? { fontSize: 12 } : {
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10, padding: '7px 14px', color: '#fff', fontSize: 12,
                        fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      {status === 'going' && <><Check size={12} /> going</>}
                      {status === 'maybe' && <><HelpCircle size={12} /> maybe</>}
                      {status === 'notGoing' && <><XCircle size={12} /> not going</>}
                    </button>
                  ))}
                  {userRsvp && (
                    <button
                      onClick={handleWithdraw}
                      disabled={actionLoading}
                      style={{
                        background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8, padding: '6px 12px', color: 'rgba(220,200,240,0.6)',
                        fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      withdraw
                    </button>
                  )}
                </div>

                {/* RSVP summary */}
                <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'rgba(220,200,240,0.5)', paddingTop: 6 }}>
                  <span>{goingCount} going</span>
                  <span>{maybeCount} maybe</span>
                </div>

                {/* Attendee lists */}
                {rsvps.filter((r) => r.status === 'going').length > 0 && (
                  <div style={{ padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 14 }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#fff' }}>
                      going ({rsvps.filter((r) => r.status === 'going').length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rsvps
                        .filter((r) => r.status === 'going')
                        .slice(0, 10)
                        .map((r) => (
                          <div
                            key={r.userId}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                            onClick={() => navigate(`/user/${r.userId}`)}
                          >
                            <MonoAvatar name={r.metadata?.userName || '?'} size={32} />
                            <span style={{ fontSize: 13, color: '#EDEDED' }}>
                              {r.metadata?.userName || 'Unknown'}
                            </span>
                          </div>
                        ))}
                      {rsvps.filter((r) => r.status === 'going').length > 10 && (
                        <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.4)' }}>
                          +{rsvps.filter((r) => r.status === 'going').length - 10} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {rsvps.filter((r) => r.status === 'maybe').length > 0 && (
                  <div style={{ padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#fff' }}>
                      maybe ({rsvps.filter((r) => r.status === 'maybe').length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rsvps
                        .filter((r) => r.status === 'maybe')
                        .slice(0, 5)
                        .map((r) => (
                          <div
                            key={r.userId}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
                            onClick={() => navigate(`/user/${r.userId}`)}
                          >
                            <MonoAvatar name={r.metadata?.userName || '?'} size={32} />
                            <span style={{ fontSize: 13, color: '#EDEDED' }}>
                              {r.metadata?.userName || 'Unknown'}
                            </span>
                          </div>
                        ))}
                      {rsvps.filter((r) => r.status === 'maybe').length > 5 && (
                        <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.4)' }}>
                          +{rsvps.filter((r) => r.status === 'maybe').length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Admin actions */}
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 10, marginTop: 20, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <button
                      onClick={() => openEditForm(eventDetail)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8, padding: '7px 14px', color: '#fff', fontSize: 12,
                        fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <Edit2 size={12} /> edit
                    </button>
                    <button
                      onClick={() => handleCancel(eventDetail.id)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 8, padding: '7px 14px', color: 'rgba(220,200,240,0.6)', fontSize: 12,
                        cursor: 'pointer', fontFamily: 'inherit',
                      }}
                    >
                      <Slash size={12} /> cancel event
                    </button>
                  </div>
                )}
              </div>
            </GlassPanel>
          )}
        </div>
      </div>
    );
  }

  // ─── Create / Edit form ───────────────────────────────────────────────
  if (showForm) {
    return (
      <div className="page-shell">
        <div className="page-shell__inner" style={{ maxWidth: 720 }}>
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={() => setShowForm(false)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 999, padding: '7px 14px', color: '#fff', fontSize: 13,
                fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <ChevronLeft size={14} /> back
            </button>
          </div>

          <GlassPanel hover={false}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: '0 0 20px' }}>
              {editingEvent ? 'edit event' : 'create event'}
            </h2>

            {formError && <div className="circle-message-banner error" style={{ marginBottom: 16 }}>{formError}</div>}

            <div className="circle-form-label">
              title
              <input
                type="text"
                className="circle-form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                maxLength={100}
                placeholder="Event title (3-100 chars)"
              />
            </div>

            <div className="circle-form-label">
              description
              <textarea
                className="circle-form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                maxLength={1000}
                placeholder="What's the event about? (10-1000 chars)"
                rows={4}
              />
            </div>

            <div className="circle-form-label">
              date &amp; time
              <input
                type="datetime-local"
                className="circle-form-input"
                value={formData.dateTime}
                onChange={(e) => setFormData({ ...formData, dateTime: e.target.value })}
              />
            </div>

            <div className="circle-form-label">
              location type
              <select
                className="circle-form-select"
                value={formData.locationType}
                onChange={(e) => setFormData({ ...formData, locationType: e.target.value as any })}
              >
                <option value="tbd">TBD</option>
                <option value="physical">physical</option>
                <option value="virtual">virtual</option>
              </select>
            </div>

            {formData.locationType !== 'tbd' && (
              <div className="circle-form-label">
                location
                <input
                  type="text"
                  className="circle-form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder={formData.locationType === 'virtual' ? 'Meeting link' : 'Address'}
                />
              </div>
            )}

            <div className="circle-form-label">
              max attendees (optional)
              <input
                type="number"
                className="circle-form-input"
                value={formData.maxAttendees}
                onChange={(e) => setFormData({ ...formData, maxAttendees: e.target.value })}
                min={2}
                max={50}
                placeholder="2-50"
              />
            </div>

            <div className="circle-form-label">
              recurrence
              <select
                className="circle-form-select"
                value={formData.recurrence}
                onChange={(e) => setFormData({ ...formData, recurrence: e.target.value as any })}
              >
                <option value="none">one-time</option>
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="monthly">monthly</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12, padding: '10px 18px', color: '#fff', fontSize: 13.5,
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                cancel
              </button>
              <button className="primary-button" onClick={submitForm} disabled={actionLoading}>
                {actionLoading ? 'saving...' : editingEvent ? 'save changes' : 'create event'}
              </button>
            </div>
          </GlassPanel>
        </div>
      </div>
    );
  }

  // ─── Events list ──────────────────────────────────────────────────────
  return (
    <div className="page-shell">
      <div className="page-shell__inner" style={{ maxWidth: 780 }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        }}>
          <button
            onClick={() => navigate(`/circles/${circleSlug}`, { state: { circleId } })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 999, padding: '7px 12px', color: '#fff', fontSize: 13,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <ChevronLeft size={14} />
          </button>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.025em' }}>
              events
            </h2>
            {circle && <span style={{ fontSize: 12, color: 'rgba(220,200,240,0.5)' }}>{circle.name}</span>}
          </div>
          {isAdmin && (
            <button className="primary-button" onClick={openCreateForm} style={{ fontSize: 12 }}>
              <Plus size={14} /> new event
            </button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2].map((i) => (
              <GlassPanel key={i} hover={false}>
                <div className="skel-line wide pulse" style={{ height: 16, marginBottom: 8 }} />
                <div className="skel-line medium pulse" style={{ height: 12 }} />
              </GlassPanel>
            ))}
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(220,200,240,0.7)' }}>
            <p>{error}</p>
          </div>
        )}

        {/* Event cards */}
        {!loading && !error && isMember && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {events.length === 0 && (
              <div style={{
                textAlign: 'center', padding: '4rem 2rem',
                color: 'rgba(220,200,240,0.4)',
              }}>
                <Calendar size={32} style={{ marginBottom: 10 }} />
                <p>no upcoming events</p>
                {isAdmin && (
                  <button className="primary-button" onClick={openCreateForm} style={{ marginTop: 12 }}>
                    create the first event
                  </button>
                )}
              </div>
            )}

            {events.map((ev) => {
              const isPast = ev.dateTime ? new Date(ev.dateTime as any).getTime() < Date.now() : false;
              return (
                <GlassPanel
                  key={ev.id}
                  onClick={() => navigate(`/circles/${circleSlug}/events/${ev.id}`, { state: { circleId } })}
                  style={{ opacity: isPast ? 0.55 : 1 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <h3 style={{ fontSize: 15, fontWeight: 600, color: '#fff', margin: 0 }}>{ev.title}</h3>
                    {ev.recurrence && ev.recurrence.type !== 'none' && (
                      <span className="tag-pill tag-pill--sm tag-pill--violet">{ev.recurrence.type}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'rgba(220,200,240,0.6)', marginBottom: 6 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={13} /> {ev.dateTime ? formatTimestamp(ev.dateTime) : '\u2014'}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={13} /> {ev.locationType === 'virtual' ? 'Virtual' : ev.locationType === 'physical' ? (ev.location || 'Physical') : 'TBD'}
                    </span>
                    {ev.maxAttendees && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Users size={13} /> max {ev.maxAttendees}
                      </span>
                    )}
                  </div>
                  {ev.description && (
                    <p style={{ fontSize: 13, color: 'rgba(220,200,240,0.5)', lineHeight: 1.4, margin: 0 }}>
                      {ev.description.length > 120 ? ev.description.slice(0, 120) + '\u2026' : ev.description}
                    </p>
                  )}
                </GlassPanel>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
